/** `/sessions/new`: "Ouvrir une session" — pick a start point, then open it. */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createSession, getCommunes } from '../../../api/sessions';
import { Button, Card, EmptyState, Input, Skeleton, useToast } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { StartPointPicker } from '../components/StartPointPicker';
import { sessionErrorMessage } from '../errors';
import { useOptionalPosition } from '../hooks/useOptionalPosition';

import type { GeoPoint } from '../../../api/sessions';
import type { Commune, SessionCreateRequest } from '../../../api/types';

type SessionKind = SessionCreateRequest['kind'];
type SessionVisibility = SessionCreateRequest['visibility'];

/**
 * Render the "Ouvrir une session" screen.
 *
 * @returns The page element.
 */
export function OpenSessionPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { position, requestPosition } = useOptionalPosition();

  const [commune, setCommune] = useState<Commune | null>(null);
  const [loadingCommune, setLoadingCommune] = useState(true);
  const [manualPoint, setManualPoint] = useState<GeoPoint | null>(null);
  const [kind, setKind] = useState<SessionKind>('standard');
  const [visibility, setVisibility] = useState<SessionVisibility>('group');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCommunes()
      .then((page) => {
        if (!cancelled) {
          setCommune(page.results[0] ?? null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          showToast(sessionErrorMessage(error), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCommune(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const point = manualPoint ?? position ?? commune?.centroid ?? null;

  async function handleSubmit() {
    if (!commune) {
      return;
    }
    if (!point) {
      showToast(t('session.open.missingPoint'), 'error');
      return;
    }
    if (!name.trim()) {
      showToast(t('session.open.missingName'), 'error');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createSession({
        commune: commune.insee_code,
        name: name.trim(),
        kind,
        visibility,
        start_point: point,
        start_address: address.trim(),
      });
      navigate(`/sessions/${created.id}`);
    } catch (error) {
      showToast(sessionErrorMessage(error), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="pile">
      <Card>
        <h2>{t('session.open.title')}</h2>
        <p className="muted" style={{ margin: '0 0 var(--space-3)' }}>
          {t('session.open.lead')}
        </p>
        {loadingCommune ? (
          <Skeleton height="260px" />
        ) : commune?.centroid ? (
          <StartPointPicker center={commune.centroid} point={point} onSelect={setManualPoint} />
        ) : (
          <EmptyState title={t('session.open.noCommune')} body={t('session.open.mapUnavailable')} />
        )}
        {point ? (
          <p className="muted" style={{ margin: 'var(--space-2) 0 0' }}>
            {t('session.open.pointPlaceholder', {
              longitude: point.longitude.toFixed(4),
              latitude: point.latitude.toFixed(4),
            })}
          </p>
        ) : null}
        <Button
          variant="secondary"
          fullWidth
          style={{ marginTop: 'var(--space-3)' }}
          onClick={requestPosition}
        >
          {t('session.open.useMyPosition')}
        </Button>
        <Input
          label={t('session.open.addressLabel')}
          placeholder={t('session.open.addressPlaceholder')}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
        />
      </Card>

      <Card>
        <p className="kicker">{t('session.open.kindKicker')}</p>
        <div className="choix">
          <button
            type="button"
            className={kind === 'standard' ? 'on' : ''}
            aria-pressed={kind === 'standard'}
            onClick={() => setKind('standard')}
          >
            {t('session.open.kindStandard')}
          </button>
          <button
            type="button"
            className={kind === 'registration' ? 'on' : ''}
            aria-pressed={kind === 'registration'}
            onClick={() => setKind('registration')}
          >
            {t('session.open.kindRegistration')}
          </button>
        </div>
        <p className="muted" style={{ margin: 'var(--space-2) 0 0' }}>
          {t('session.open.kindHint')}
        </p>
      </Card>

      <Card>
        <Input
          label={t('session.open.nameLabel')}
          placeholder={t('session.open.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <p className="kicker">{t('session.open.visibilityKicker')}</p>
        <div className="choix">
          <button
            type="button"
            className={visibility === 'group' ? 'on' : ''}
            aria-pressed={visibility === 'group'}
            onClick={() => setVisibility('group')}
          >
            {t('session.open.visibilityGroup')}
          </button>
          <button
            type="button"
            className={visibility === 'invite' ? 'on' : ''}
            aria-pressed={visibility === 'invite'}
            onClick={() => setVisibility('invite')}
          >
            {t('session.open.visibilityInvite')}
          </button>
        </div>
        <p className="muted" style={{ margin: 'var(--space-2) 0 0' }}>
          {t('session.open.visibilityHint')}
        </p>
      </Card>

      <Card>
        <div className="pile">
          <Button
            variant="primary"
            fullWidth
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {t('session.open.submit')}
          </Button>
          <Button variant="ghost" fullWidth onClick={() => navigate('/')}>
            {t('session.open.cancel')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
